import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import dico from "../../../util/src/language/dico";
import { EDITOR_PICKER_POPOVER_Z_INDEX } from "./editorPickerPopoverZIndex";

export type SpacingScope = "all" | "top" | "right" | "bottom" | "left";

export type SpacingControl = {
  scope: SpacingScope;
  value: number;
  sides?: Record<Exclude<SpacingScope, "all">, number>;
};

export type SpacingConfig = {
  margin: SpacingControl;
  padding: SpacingControl;
};

type SpacingPickerProps = {
  value: SpacingConfig;
  disabled?: boolean;
  onChange: (nextConfig: SpacingConfig) => void;
};

const DEFAULT_SPACING_CONTROL: SpacingControl = {
  scope: "all",
  value: 0,
};

const DEFAULT_SPACING_CONFIG: SpacingConfig = {
  margin: DEFAULT_SPACING_CONTROL,
  padding: DEFAULT_SPACING_CONTROL,
};

const SPACING_SCOPES: SpacingScope[] = [
  "all",
  "top",
  "right",
  "bottom",
  "left",
];

/**
 * Renders a compact spacing editor with a box-model preview and popover controls.
 *
 * The picker edits margin and padding values using scope-based controls.
 */
const SpacingPicker: React.FC<SpacingPickerProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [marginScope, setMarginScope] = useState<SpacingScope>(
    value?.margin?.scope ?? DEFAULT_SPACING_CONFIG.margin.scope,
  );
  const [paddingScope, setPaddingScope] = useState<SpacingScope>(
    value?.padding?.scope ?? DEFAULT_SPACING_CONFIG.padding.scope,
  );
  const labels = (dico().licensedPortal?.layoutEditor as {
    spacingPicker?: Record<string, string>;
    borderPicker?: Record<string, string>;
  }) ?? { spacingPicker: {}, borderPicker: {} };

  const mergedValue = useMemo<SpacingConfig>(
    () => ({
      margin: {
        scope: value?.margin?.scope ?? DEFAULT_SPACING_CONFIG.margin.scope,
        value: Number.isFinite(value?.margin?.value)
          ? Math.max(0, value.margin.value)
          : DEFAULT_SPACING_CONFIG.margin.value,
        sides: normalizeSideValues(value?.margin),
      },
      padding: {
        scope: value?.padding?.scope ?? DEFAULT_SPACING_CONFIG.padding.scope,
        value: Number.isFinite(value?.padding?.value)
          ? Math.max(0, value.padding.value)
          : DEFAULT_SPACING_CONFIG.padding.value,
        sides: normalizeSideValues(value?.padding),
      },
    }),
    [value],
  );

  const popupOpen = Boolean(anchorEl);

  useEffect(() => {
    if (!popupOpen) {
      setMarginScope(
        value?.margin?.scope ?? DEFAULT_SPACING_CONFIG.margin.scope,
      );
      setPaddingScope(
        value?.padding?.scope ?? DEFAULT_SPACING_CONFIG.padding.scope,
      );
    }
  }, [popupOpen, value?.margin?.scope, value?.padding?.scope]);

  const mergedValueWithScope = useMemo<SpacingConfig>(
    () => ({
      margin: { ...mergedValue.margin, scope: marginScope },
      padding: { ...mergedValue.padding, scope: paddingScope },
    }),
    [marginScope, mergedValue.margin, mergedValue.padding, paddingScope],
  );

  const marginPreviewInsets = toInsets(mergedValueWithScope.margin);
  const paddingPreviewInsets = toInsets(mergedValueWithScope.padding);
  const marginInputValue = getControlValueForScope(
    mergedValueWithScope.margin,
    marginPreviewInsets,
  );
  const paddingInputValue = getControlValueForScope(
    mergedValueWithScope.padding,
    paddingPreviewInsets,
  );

  const marginPx = {
    top: `${scalePreviewInset(marginPreviewInsets.top)}px`,
    right: `${scalePreviewInset(marginPreviewInsets.right)}px`,
    bottom: `${scalePreviewInset(marginPreviewInsets.bottom)}px`,
    left: `${scalePreviewInset(marginPreviewInsets.left)}px`,
  };

  const paddingPx = {
    top: `${scalePreviewInset(paddingPreviewInsets.top)}px`,
    right: `${scalePreviewInset(paddingPreviewInsets.right)}px`,
    bottom: `${scalePreviewInset(paddingPreviewInsets.bottom)}px`,
    left: `${scalePreviewInset(paddingPreviewInsets.left)}px`,
  };

  const updateControl = (
    key: "margin" | "padding",
    partial: Partial<SpacingControl>,
  ) => {
    const nextMarginScope =
      key === "margin" ? (partial.scope ?? marginScope) : marginScope;
    const nextPaddingScope =
      key === "padding" ? (partial.scope ?? paddingScope) : paddingScope;
    if (key === "margin" && partial.scope) {
      setMarginScope(partial.scope);
    }
    if (key === "padding" && partial.scope) {
      setPaddingScope(partial.scope);
    }

    const currentMarginControl = mergedValue.margin;
    const currentPaddingControl = mergedValue.padding;
    const nextMarginControl =
      key === "margin"
        ? updateSpacingControl(
            currentMarginControl,
            nextMarginScope,
            partial.value,
          )
        : currentMarginControl;
    const nextPaddingControl =
      key === "padding"
        ? updateSpacingControl(
            currentPaddingControl,
            nextPaddingScope,
            partial.value,
          )
        : currentPaddingControl;

    onChange({
      margin: nextMarginControl,
      padding: nextPaddingControl,
    });
  };

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(event) => {
          if (!disabled) {
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setAnchorEl(event.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 30,
            m: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "8px",
              border: "1px dashed #f59e0b",
              background: "rgba(245, 158, 11, 0.1)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: marginPx.top,
              right: marginPx.right,
              bottom: marginPx.bottom,
              left: marginPx.left,
              borderRadius: "8px",
              border: "1px dashed #10b981",
              background: "rgba(16, 185, 129, 0.12)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: `calc(${marginPx.top} + ${paddingPx.top})`,
              right: `calc(${marginPx.right} + ${paddingPx.right})`,
              bottom: `calc(${marginPx.bottom} + ${paddingPx.bottom})`,
              left: `calc(${marginPx.left} + ${paddingPx.left})`,
              borderRadius: "6px",
              border: "1px solid #94a3b8",
              background: "#ffffff",
            }}
          />
        </Box>
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 320,
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#1f2937",
              }}
            >
              {labels.spacingPicker?.title ?? ""}
            </Typography>
            <Tooltip title={labels.spacingPicker?.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() =>
                    onChange({
                      margin: {
                        scope: "all",
                        value: 0,
                        sides: { top: 0, right: 0, bottom: 0, left: 0 },
                      },
                      padding: {
                        scope: "all",
                        value: 0,
                        sides: { top: 0, right: 0, bottom: 0, left: 0 },
                      },
                    })
                  }
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.spacingPicker?.marginScopeLabel ?? ""}
            </Typography>
            <Box sx={toggleGroupSx}>
              {SPACING_SCOPES.map((entry) => (
                <Box
                  key={`margin-${entry}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => updateControl("margin", { scope: entry })}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      updateControl("margin", { scope: entry });
                    }
                  }}
                  sx={[chipSx, marginScope === entry ? chipActiveSx : null]}
                >
                  {scopeLabel(entry, labels)}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.spacingPicker?.marginValueLabel ?? ""}
            </Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={marginInputValue}
              onChange={(event) =>
                updateControl("margin", {
                  value: Number(event.target.value) || 0,
                })
              }
              sx={inputSx}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.spacingPicker?.paddingScopeLabel ?? ""}
            </Typography>
            <Box sx={toggleGroupSx}>
              {SPACING_SCOPES.map((entry) => (
                <Box
                  key={`padding-${entry}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => updateControl("padding", { scope: entry })}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      updateControl("padding", { scope: entry });
                    }
                  }}
                  sx={[chipSx, paddingScope === entry ? chipActiveSx : null]}
                >
                  {scopeLabel(entry, labels)}
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>
              {labels.spacingPicker?.paddingValueLabel ?? ""}
            </Typography>
            <TextField
              size="small"
              type="number"
              fullWidth
              value={paddingInputValue}
              onChange={(event) =>
                updateControl("padding", {
                  value: Number(event.target.value) || 0,
                })
              }
              sx={inputSx}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const scopeLabel = (
  scope: SpacingScope,
  labels: {
    spacingPicker?: Record<string, string>;
    borderPicker?: Record<string, string>;
  },
): string => {
  const scoped = labels.spacingPicker ?? {};
  const generic = labels.borderPicker ?? {};
  if (scope === "all") {
    return scoped.scopeAll ?? generic.scopeAll ?? "";
  }
  const key = `scope${scope.charAt(0).toUpperCase()}${scope.slice(1)}`;
  return scoped[key] ?? generic[key] ?? "";
};

const toInsets = (
  control: SpacingControl,
): Record<Exclude<SpacingScope, "all">, number> => {
  const next = normalizeSideValues(control);
  const normalized = Math.max(0, Math.round(control.value));
  if (control.sides) {
    return next;
  }
  if (control.scope === "all") {
    next.top = normalized;
    next.right = normalized;
    next.bottom = normalized;
    next.left = normalized;
    return next;
  }
  next[control.scope] = normalized;
  return next;
};

const normalizeSideValues = (
  control?: Partial<SpacingControl> | null,
): Record<Exclude<SpacingScope, "all">, number> => {
  const rawSides = control?.sides;
  const normalizedFallback =
    typeof control?.value === "number" && Number.isFinite(control.value)
      ? Math.max(0, Math.round(control.value))
      : 0;

  const top = Math.max(
    0,
    Math.round(
      typeof rawSides?.top === "number" && Number.isFinite(rawSides.top)
        ? rawSides.top
        : normalizedFallback,
    ),
  );
  const right = Math.max(
    0,
    Math.round(
      typeof rawSides?.right === "number" && Number.isFinite(rawSides.right)
        ? rawSides.right
        : normalizedFallback,
    ),
  );
  const bottom = Math.max(
    0,
    Math.round(
      typeof rawSides?.bottom === "number" && Number.isFinite(rawSides.bottom)
        ? rawSides.bottom
        : normalizedFallback,
    ),
  );
  const left = Math.max(
    0,
    Math.round(
      typeof rawSides?.left === "number" && Number.isFinite(rawSides.left)
        ? rawSides.left
        : normalizedFallback,
    ),
  );

  return { top, right, bottom, left };
};

const getControlValueForScope = (
  control: SpacingControl,
  insets: Record<Exclude<SpacingScope, "all">, number>,
): number => {
  if (control.scope === "all") {
    return Math.max(0, Math.round(control.value));
  }
  return Math.max(0, Math.round(insets[control.scope]));
};

const updateSpacingControl = (
  control: SpacingControl,
  scope: SpacingScope,
  nextValue?: number,
): SpacingControl => {
  const normalizedSides = normalizeSideValues(control);
  const hasNextValue =
    typeof nextValue === "number" && Number.isFinite(nextValue);
  const normalizedValue = hasNextValue
    ? Math.max(0, Math.round(nextValue))
    : undefined;
  const nextSides = { ...normalizedSides };

  if (normalizedValue !== undefined) {
    if (scope === "all") {
      nextSides.top = normalizedValue;
      nextSides.right = normalizedValue;
      nextSides.bottom = normalizedValue;
      nextSides.left = normalizedValue;
    } else {
      nextSides[scope] = normalizedValue;
    }
  }

  return {
    scope,
    value: scope === "all" ? nextSides.top : nextSides[scope],
    sides: nextSides,
  };
};

const scalePreviewInset = (value: number): number => {
  if (value <= 0) {
    return 0;
  }
  return Math.min(9, Math.round(value * 0.45));
};

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const toggleGroupSx = {
  display: "flex",
  gap: "5px",
  flexWrap: "wrap",
};

const chipSx = {
  minWidth: 0,
  height: 28,
  padding: "0 10px",
  borderRadius: "6px",
  border: "1px solid #c7d2e0",
  background: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  color: "#334155",
  cursor: "pointer",
  userSelect: "none",
  "&:hover": {
    borderColor: "#94a3b8",
    background: "#f1f5f9",
  },
};

const chipActiveSx = {
  borderColor: "#4f9cff",
  color: "#0f3f79",
  fontWeight: 600,
  background: "rgba(79, 156, 255, 0.18)",
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 42,
    borderRadius: "6px",
    "& input": {
      fontSize: 14,
    },
  },
};

export default SpacingPicker;
